import { SOLANA, TON } from "consts";
import axiosService from "services/axios";
import { useTxnSign } from "hooks/useTxnSign";
import { DarkDrawer } from "components/TradeDrawer";
import { Divider, MenuItem, Select, Typography } from "@mui/material";
import { useCallback, useEffect, useMemo, useRef, useState } from "react";
import useToastStore from "store/toast-store/useToastStore";
import useUserStoreV2 from "store/user-store-v2/useUserStoreV2";
import { minimumGasBufferAmount, minimumGasBufferUSD } from "tradeConfigs";
import { Chain, CHAIN_CHECK_LIST, EVM_CHAINS, NATIVE_CURRENCY_CHECK_LIST } from "chainConfigs";
import { Box, Drawer, useTheme } from "@mui/material";
import { CircleButton } from "components/CircleButton";
import { CHAIN_DECIMAL } from "consts";
import { checkAddressChain, roundWithDecimals } from "lib/utils";
import { usePortfolioContext } from "contexts/PortfolioContext";
import useTradeStore from "store/trade-store.ts/useTradeStore";
import { erc20Abi } from "viem";
import { styled } from "@mui/material";
import { Close } from "@mui/icons-material";
import { ReactComponent as SendIcon } from "assets/icons/portfolio-send.svg";
import { Value } from "components/Value";
import { BaseButton, TextButton } from "components/Base/Buttons";
import { Card, Row } from "components/Base/Containers";
import CoinAvatar, { NATIVE_TOKEN_MAP } from "components/CoinAvatar";
import { StyledInputV2 } from "components/portfolio/styled";
import { SmallNumberView } from "components/SmallValueView/SmallValueView";

// TODO: Refactor
const StyledDrawer = styled(Drawer)(({ theme }) => ({
  "& .MuiDrawer-paper": {
    width: "100%",
    height: "100%",
    backgroundColor: theme.palette.surface.bg,
    color: theme.palette.text.primary,
    display: "flex",
    justifyContent: "start",
    alignItems: "center",
  },
}));

// TODO: Refactor
export const StyledSelect = styled(Select)<{ noPointer?: boolean }>(({ theme, noPointer }) => ({
  pointerEvents: noPointer ? "none" : "auto",
  cursor: "pointer",
  borderRadius: `${theme.spacing(1.5)} 0 0 ${theme.spacing(1.5)}`,
  overflow: "hidden",

  "& :focus-visible": {
    backgroundColor: "transparent",
    border: "none",
    outline: "none",
  },

  ".MuiSelect-select": {
    display: "flex",
    alignItems: "center",
    gap: theme.spacing(1),
    padding: theme.spacing(1, 2),
    borderRadius: `${theme.spacing(1.5)} 0 0 ${theme.spacing(1.5)}`,
    backgroundColor: theme.palette.surface.silver,
    pointerEvents: noPointer ? "none" : "auto",
    width: "100%",
  },

  "& .MuiOutlinedInput-notchedOutline": {
    border: "none",
    borderRadius: `${theme.spacing(1.5)} 0 0 ${theme.spacing(1.5)}`,
    outline: "none",
  },

  "& .MuiSelect-icon": {
    color: theme.palette.text.secondary,
    fontSize: "24px",
  },

  "&:hover": {
    "& .MuiOutlinedInput-notchedOutline": {
      border: "none",
    },
  },
}));

const MAX_SEND_THRESHOLD_MULTIPLIER = 0.99999999;
export const SendTokenDrawer = ({
  data,
  isOpen,
  close,
}: {
  data: any[];
  isOpen: boolean;
  close: () => void;
}) => {
  const {
    signAndBroadcastTonTxn,
    signAndBroadcastSolanaTxn,
    signAndBroadcastTxn,
    signAndWriteContract,
  } = useTxnSign();
  const { walletAddresses, isCustodial } = useUserStoreV2();
  const { showToast } = useToastStore();
  const { refetchUserAllPortfolioAssets } = usePortfolioContext();

  const sendingRef = useRef({ isSending: false });
  const [isSending, setIsSending] = useState(false);

  const [selectedTokenIndex, setSelectedTokenIndex] = useState(0);

  const [memo, setMemo] = useState("");
  const [address, setAddress] = useState("");
  const [isValidAddress, setIsValidAddress] = useState(false);

  const [amount, setAmount] = useState(0);
  const amountRef = useRef<HTMLInputElement>(null);

  const [isOpenSearchDrawer, setIsOpenSearchDrawer] = useState(false);
  const [isOpenConfirmDrawer, setIsOpenConfirmDrawer] = useState(false);

  const [minGasBuffer, setMinGasBuffer] = useState(0.0);
  const { getUserBalance, isLoading } = useTradeStore();

  useEffect(() => {
    if (indexedData[selectedTokenIndex]) {
      const selectedChain = indexedData[selectedTokenIndex].chain as Chain;
      const isNativeCurrency = NATIVE_CURRENCY_CHECK_LIST.includes(
        indexedData[selectedTokenIndex]?.symbol,
      );
      const minGasBuffer =
        isNativeCurrency && EVM_CHAINS.includes(selectedChain)
          ? 0
          : minimumGasBufferAmount[selectedChain] ?? 0;
      setMinGasBuffer(minGasBuffer);
    }
  }, [selectedTokenIndex]);

  useEffect(() => {
    if (amountRef.current) {
      amountRef.current?.focus();
      amountRef.current.value = "0";
      handleEnterAddress(address);
      setAmount(0);
      handleEnterMemo("");
    }
  }, [selectedTokenIndex]);

  const indexedData = useMemo(() => {
    return data
      .sort((a, b) => {
        return b.priceUsd - a.priceUsd;
      })
      .map((item, index) => {
        return {
          index,
          ...item,
        };
      });
  }, [data]);

  const theme = useTheme();

  const handleOpenSearchDrawer = () => {
    setIsOpenSearchDrawer(true);
  };

  const handleCloseSearchDrawer = () => {
    setIsOpenSearchDrawer(false);
  };

  const handleEnterAddress = (address: string) => {
    const chain = checkAddressChain(address);
    const selectedChain = EVM_CHAINS.includes(indexedData[selectedTokenIndex].chain)
      ? "evm"
      : indexedData[selectedTokenIndex].chain;

    if (chain === selectedChain) {
      setIsValidAddress(true);
      setAddress(address);
    } else {
      setIsValidAddress(false);
      setAddress(address);
    }
  };

  const handleEnterMemo = (memo: string) => {
    setMemo(memo);
  };

  const handleMaxAmount = () => {
    if (amountRef.current && indexedData[selectedTokenIndex]) {
      const MIN_AMOUNT = 0;

      const tokenBalance = indexedData[selectedTokenIndex].balance;
      const tokenUsdBalance = indexedData[selectedTokenIndex].priceUsd;
      const usdPerToken = tokenUsdBalance / tokenBalance;

      const clampedAmount =
        Math.max(MIN_AMOUNT, (tokenBalance - minGasBuffer) * MAX_SEND_THRESHOLD_MULTIPLIER) *
        usdPerToken;

      amountRef.current.value = roundWithDecimals("floor", clampedAmount, 2).toString();
      setAmount(Number(clampedAmount));
    }
  };

  const handleOpenConfirmDrawer = () => {
    setIsOpenConfirmDrawer(true);
  };

  const handleCloseConfirmDrawer = () => {
    setIsSending(false);
    setIsOpenConfirmDrawer(false);
  };

  const handleConfirm = async () => {
    if (sendingRef.current.isSending || !indexedData[selectedTokenIndex]) {
      return;
    }

    let res;
    try {
      showToast({
        variant: "info",
        message: "Processing now. Please wait.",
      });

      handleCloseConfirmDrawer();
      close();

      const chain = indexedData[selectedTokenIndex].chain;

      // calculate the decimal amount from USD amount
      const amountInDecimal =
        (indexedData[selectedTokenIndex].balance / indexedData[selectedTokenIndex].priceUsd) *
        amount;

      if (NATIVE_CURRENCY_CHECK_LIST.includes(indexedData[selectedTokenIndex].symbol)) {
        const isMax = indexedData[selectedTokenIndex]?.balance >= amount;

        let boc: string | undefined;
        let hash: string | undefined;
        let signedTxn: string[] | undefined;

        if (!isCustodial) {
          if (chain === TON) {
            boc = await signAndBroadcastTonTxn(address, amount, memo, isMax);
          } else if (chain === SOLANA && walletAddresses?.solAddress) {
            const txnMessage = await axiosService.getTxnMessage({
              chain,
              amount: amountInDecimal,
              receiverAddress: address,
              isMax,
            });
            signedTxn = await signAndBroadcastSolanaTxn(walletAddresses?.solAddress, txnMessage);
          } else if (walletAddresses?.ethAddress) {
            const calldata = {
              calldatas: {
                from: walletAddresses?.ethAddress,
                to: address,
              },
              value: (amountInDecimal * CHAIN_DECIMAL.ethereum).toString(),
            };
            try {
              hash = await signAndBroadcastTxn(walletAddresses?.ethAddress, chain, calldata);
            } catch (error) {
              console.error("Error while sending token", error);
              return showToast({
                variant: "error",
                message: "Insufficient balance",
                duration: 5000,
              });
            }
          }
        }

        if (isCustodial || boc || signedTxn || hash) {
          res = await axiosService.sendToken({
            receiverAddress: address,
            amount: amountInDecimal,
            signedTxn,
            chain,
            hash,
            boc,
            isMax,
          });
        }
      } else {
        let signedTxn: string[] | undefined;
        let hash: string | undefined;
        const tokenAddress = indexedData[selectedTokenIndex].tokenAddress;
        const isMax = indexedData[selectedTokenIndex]?.balance === amount;

        if (!isCustodial) {
          if (chain === SOLANA && walletAddresses?.solAddress) {
            let txnMessage: any = await axiosService.getTxnMessage({
              chain,
              amount: amountInDecimal,
              tokenAddress,
              receiverAddress: address,
              isMax,
            });

            if (txnMessage.isNewTokenAcc) {
              signedTxn = await signAndBroadcastSolanaTxn(
                walletAddresses?.solAddress,
                txnMessage.versionedTransaction,
              );

              const res = await axiosService.sendToken({
                receiverAddress: address,
                signedTxn,
                amount: amountInDecimal,
                chain,
                isMax,
              });

              if (res.status < 300) {
                txnMessage = await axiosService.getTxnMessage({
                  chain,
                  amount: amountInDecimal,
                  tokenAddress,
                  receiverAddress: address,
                  isMax,
                });
              }
            }

            signedTxn = await signAndBroadcastSolanaTxn(walletAddresses?.solAddress, txnMessage);
          } else if (walletAddresses?.ethAddress) {
            const data: any = await axiosService.getTxnMessage({
              chain,
              amount: amountInDecimal,
              tokenAddress,
              receiverAddress: address,
            });
            if (data.value) {
              const calldata = {
                address: tokenAddress,
                abi: erc20Abi,
                functionName: "transfer",
                args: [address, Number(data.value)],
              };
              hash = await signAndWriteContract(chain, walletAddresses?.ethAddress, calldata);
            }
          }
        }

        res = await axiosService.sendToken({
          receiverAddress: address,
          chain: indexedData[selectedTokenIndex].chain,
          amount: amountInDecimal,
          tokenAddress,
          signedTxn,
          hash,
          isMax,
        });
      }

      if (res.status < 300) {
        showToast({
          variant: "success",
          message: `${res.message}`,
          duration: 5000,
        });
      } else {
        showToast({
          variant: "error",
          message: res.message,
          duration: 5000,
        });
      }
    } catch (error) {
      showToast({
        variant: "error",
        message: res ? res.message : error,
        duration: 5000,
      });
      console.error("Error while sending token", error);
    } finally {
      setIsSending(false);
      refetchUserAllPortfolioAssets();
    }
  };

  // useEffect(() => {
  //   if (!indexedData[selectedTokenIndex]) {
  //     console.log("loading");
  //   } else {
  //     const check_1 = (getUserBalance(indexedData[selectedTokenIndex].chain, "amount") ?? 0) -
  //       minGasBuffer <=
  //       0
  //     const check_2 = (NATIVE_CURRENCY_CHECK_LIST.includes(indexedData[selectedTokenIndex]?.symbol) &&
  //     amount >
  //       (((getUserBalance(indexedData[selectedTokenIndex].chain, "amount") ?? 0) -
  //         minGasBuffer) *
  //         indexedData[selectedTokenIndex].priceUsd) /
  //         indexedData[selectedTokenIndex].balance)
  //     const check_3 = !NATIVE_CURRENCY_CHECK_LIST.includes(indexedData[selectedTokenIndex]?.symbol) &&
  //     amount > indexedData[selectedTokenIndex].priceUsd

  //     if (check_1) {
  //       console.log("Gas Buffer Not Enough")
  //       console.log({check_1})
  //       console.log({token_amount: (getUserBalance(indexedData[selectedTokenIndex].chain, "amount") ?? 0)})
  //       console.log({min_gas_buffer: minGasBuffer})
  //       console.log({delta_gas_buffer_exceed: (getUserBalance(indexedData[selectedTokenIndex].chain, "amount") ?? 0) -
  //         minGasBuffer})
  //     } else if (check_2) {
  //       console.log({check_2})
  //     } else if (check_3) {
  //       console.log("Not Enoungh Balance")
  //       console.log({check_3})
  //       console.log({input_usd: amount})
  //       console.log({usd_balance: indexedData[selectedTokenIndex].priceUsd})
  //       console.log({delta_usd_exceed: amount - indexedData[selectedTokenIndex].priceUsd})
  //       console.log({rounded_usd: roundWithDecimals("floor", indexedData[selectedTokenIndex].priceUsd, 2)})
  //     }
  //   }
  // }, [!isValidAddress, amount, indexedData[selectedTokenIndex]]);

  return (
    <>
      <StyledDrawer
        open={isOpen && !isLoading}
        anchor="bottom"
        onClose={() => {}}
        sx={{ zIndex: 200 }}>
        <Box
          sx={{
            display: "flex",
            justifyContent: "center",
            alignItems: "center",
            flexDirection: "column",
            width: "100%",
            height: "100%",
          }}>
          <Box sx={{ width: "100%", height: "100%", padding: "20px 20px 0" }}>
            <Box
              sx={{
                display: "flex",
                justifyContent: "end",
                alignItems: "center",
                width: "100%",
              }}>
              <CircleButton variant="white" onClick={close}>
                <Close fontSize="inherit" />
              </CircleButton>
            </Box>
            <Box
              sx={{
                display: "flex",
                justifyContent: "center",
                alignItems: "center",
                flexDirection: "column",
                width: "100%",
                textAlign: "center",
                gap: "9px",
                marginY: "9px",
              }}>
              <SendIcon />
              <Typography variant="title-h1" color="text.primary">
                SEND TOKENS
              </Typography>
            </Box>
            <Box
              sx={{
                display: "flex",
                justifyContent: "center",
                alignItems: "center",
                width: "100%",
                gap: "12px",
                flexDirection: "column",
              }}>
              <Card._3D cardType="dark">
                <Box
                  sx={{
                    display: "flex",
                    justifyContent: "center",
                    alignItems: "center",
                    width: "100%",
                    flexDirection: "column",
                    gap: "8px",
                    padding: "20px",
                  }}>
                  <Box
                    sx={{
                      display: "flex",
                      justifyContent: "space-between",
                      alignItems: "center",
                      width: "100%",
                    }}>
                    <Box
                      sx={{
                        display: "flex",
                        alignItems: "center",
                        gap: "4px",
                      }}>
                      <Typography variant="body-default" color="text.secondary">
                        Balance:{" "}
                      </Typography>
                      {NATIVE_CURRENCY_CHECK_LIST.includes(
                        indexedData[selectedTokenIndex]?.symbol,
                      ) ? (
                        <>
                          <Value
                            variant="body-default"
                            color="text.secondary"
                            value={indexedData[selectedTokenIndex].priceUsd}
                            smallNumberSignificantDigits={2}
                            isPrice
                          />{" "}
                          <Typography variant="body-default" color="text.secondary">
                            USD
                          </Typography>
                        </>
                      ) : (
                        <>
                          <Value
                            variant="body-default"
                            color="text.secondary"
                            value={indexedData[selectedTokenIndex]?.priceUsd}
                            smallNumberSignificantDigits={2}
                            isPrice
                          />{" "}
                          <Typography variant="body-default" color="text.secondary">
                            USD
                          </Typography>
                        </>
                      )}
                    </Box>
                    <Box sx={{ width: "fit-content" }}>
                      <BaseButton onClick={handleMaxAmount}>
                        <Box sx={{ padding: "4px 12px" }}>
                          <Typography variant="button-md">MAX</Typography>
                        </Box>
                      </BaseButton>
                    </Box>
                  </Box>
                  <Box
                    sx={{
                      display: "flex",
                      justifyContent: "space-between",
                      alignItems: "center",
                      width: "100%",
                      backgroundColor: theme.palette.surface.silver,
                      borderRadius: theme.spacing(1.5),
                    }}>
                    <Box
                      sx={{
                        display: "flex",
                      }}
                      onClick={handleOpenSearchDrawer}>
                      <StyledSelect
                        noPointer
                        value={selectedTokenIndex}
                        defaultValue={0}
                        sx={{ display: "flex", alignItems: "center", gap: theme.spacing(1) }}
                        MenuProps={{
                          slotProps: {
                            paper: {
                              sx: {
                                borderRadius: theme.spacing(1.5),
                                backgroundColor: theme.palette.surface.silver,
                              },
                            },
                          },
                        }}
                        onChange={(event) => setSelectedTokenIndex(event.target.value as number)}
                        IconComponent={undefined}>
                        {indexedData.map((token) => (
                          <MenuItem
                            key={token.index}
                            style={{ width: "100%" }}
                            selected={selectedTokenIndex === token.index}
                            value={token.index}>
                            <Box
                              sx={{
                                display: "flex",
                                alignItems: "center",
                                justifyContent: "space-between",
                                width: "100%",
                              }}>
                              <Box
                                sx={{
                                  display: "flex",
                                  alignItems: "center",
                                  gap: theme.spacing(1.5),
                                }}>
                                <CoinAvatar
                                  style={{
                                    width: "28px",
                                    height: "28px",
                                  }}
                                  badgeStyle={{
                                    width: "14px",
                                    height: "14px",
                                  }}
                                  iconUri={
                                    token.imageUrl ? token.imageUrl : NATIVE_TOKEN_MAP[token.symbol]
                                  }
                                  chain={token.chain}
                                  size="sm"
                                />
                                <Typography
                                  variant="body-default"
                                  color={theme.palette.text.secondary}>
                                  {token.symbol}
                                </Typography>
                              </Box>
                              {selectedTokenIndex === token.index ? null : (
                                <Typography
                                  variant="body-default"
                                  color={theme.palette.text.secondary}>
                                  <SmallNumberView value={token.balance} isUSD />
                                </Typography>
                              )}
                            </Box>
                          </MenuItem>
                        ))}
                      </StyledSelect>
                      <Divider
                        orientation="vertical"
                        variant="middle"
                        flexItem
                        sx={{
                          border: `0.5px solid ${theme.palette.surface["light-bg"]}`,
                        }}
                      />
                    </Box>
                    <StyledInputV2
                      type="number"
                      ref={amountRef}
                      min={0}
                      max={indexedData[selectedTokenIndex]?.balance}
                      placeholder="0"
                      onChange={() => {
                        if (amountRef.current?.value !== null) {
                          if (amountRef.current) {
                            setAmount(parseFloat(amountRef.current.value));
                          }
                        }
                      }}
                      onBlur={() => {
                        if (amountRef.current?.value !== null) {
                          if (amountRef.current) {
                            amountRef.current.value = Number(
                              Number(parseFloat(amountRef.current.value)).toFixed(2),
                            ).toString();
                            setAmount(
                              Number(Number(parseFloat(amountRef.current.value)).toFixed(2)),
                            );
                          }
                        }
                      }}
                    />
                    <Typography
                      variant="body-default"
                      sx={{
                        display: "inline",
                        marginRight: theme.spacing(1),
                      }}
                      color={theme.palette.text.secondary}>
                      {/* {indexedData[selectedTokenIndex]?.symbol} */}
                      USD
                    </Typography>
                  </Box>
                </Box>
              </Card._3D>
              <Card._3D cardType="dark">
                <Box
                  sx={{
                    display: "flex",
                    justifyContent: "center",
                    alignItems: "center",
                    width: "100%",
                    flexDirection: "column",
                    gap: "8px",
                    padding: "20px",
                  }}>
                  <Box
                    sx={{
                      display: "flex",
                      justifyContent: "space-between",
                      alignItems: "center",
                      width: "100%",
                    }}>
                    <Typography variant="body-default" color="text.secondary">
                      To
                    </Typography>
                  </Box>
                  <Box
                    sx={{
                      display: "flex",
                      justifyContent: "space-between",
                      alignItems: "center",
                      width: "100%",
                    }}>
                    <StyledInputV2
                      style={{ textAlign: "start" }}
                      placeholder="Enter Address"
                      onChange={(event) => handleEnterAddress(event.target.value)}
                    />
                  </Box>
                  {indexedData &&
                  indexedData[selectedTokenIndex] &&
                  indexedData[selectedTokenIndex].chain === TON ? (
                    <>
                      <Box
                        sx={{
                          display: "flex",
                          justifyContent: "space-between",
                          alignItems: "center",
                          width: "100%",
                        }}>
                        <Typography variant="body-default" color="text.secondary">
                          Memo (Optional)
                        </Typography>
                      </Box>
                      <Box
                        sx={{
                          display: "flex",
                          justifyContent: "space-between",
                          alignItems: "center",
                          width: "100%",
                        }}>
                        <StyledInputV2
                          style={{ textAlign: "start" }}
                          placeholder="Enter memo"
                          onChange={(event) => handleEnterMemo(event.target.value)}
                        />
                      </Box>
                    </>
                  ) : null}
                </Box>
              </Card._3D>
            </Box>
          </Box>
          <Box sx={{ margin: "12px 20px 20px", width: "100%", padding: "0 20px" }}>
            <BaseButton
              disabled={
                !isValidAddress ||
                amount <= 0 ||
                // Check if the balance is enough to send the token
                (getUserBalance(indexedData[selectedTokenIndex].chain, "amount") ?? 0) -
                  minGasBuffer <=
                  0 ||
                (NATIVE_CURRENCY_CHECK_LIST.includes(indexedData[selectedTokenIndex]?.symbol) &&
                  amount >
                    (((getUserBalance(indexedData[selectedTokenIndex].chain, "amount") ?? 0) -
                      minGasBuffer) *
                      indexedData[selectedTokenIndex].priceUsd) /
                      indexedData[selectedTokenIndex].balance) ||
                (!NATIVE_CURRENCY_CHECK_LIST.includes(indexedData[selectedTokenIndex]?.symbol) &&
                  amount > indexedData[selectedTokenIndex].priceUsd)
              }
              onClick={handleOpenConfirmDrawer}
              fullWidth>
              <Box
                sx={{
                  padding: "12px",
                }}>
                <Typography
                  variant="title-h3"
                  color={
                    !isValidAddress ||
                    amount <= 0 ||
                    (getUserBalance(indexedData[selectedTokenIndex].chain, "amount") ?? 0) -
                      minGasBuffer <=
                      0 ||
                    (NATIVE_CURRENCY_CHECK_LIST.includes(indexedData[selectedTokenIndex]?.symbol) &&
                      amount >
                        (((getUserBalance(indexedData[selectedTokenIndex].chain, "amount") ?? 0) -
                          minGasBuffer) *
                          indexedData[selectedTokenIndex].priceUsd) /
                          indexedData[selectedTokenIndex].balance) ||
                    (!NATIVE_CURRENCY_CHECK_LIST.includes(
                      indexedData[selectedTokenIndex]?.symbol,
                    ) &&
                      amount > indexedData[selectedTokenIndex].priceUsd)
                      ? "text.secondary"
                      : "text.brand"
                  }>
                  CONTINUE
                </Typography>
              </Box>
            </BaseButton>
          </Box>
        </Box>
      </StyledDrawer>

      {/* Search */}
      <DarkDrawer open={isOpenSearchDrawer} anchor="bottom" onClose={() => {}} sx={{ zIndex: 200 }}>
        <Box
          sx={{
            display: "flex",
            justifyContent: "space-between",
            alignItems: "center",
            width: "100%",
            marginBottom: "16px",
          }}>
          <Box sx={{ width: "28px" }} />
          <Typography variant="title-h2">SELECT TOKEN</Typography>
          <CircleButton variant="white" onClick={handleCloseSearchDrawer}>
            <Close fontSize="inherit" />
          </CircleButton>
        </Box>
        <Box
          sx={{
            overflowY: "auto",
            maxHeight: "100%",
            display: "flex",
            flexDirection: "column",
            justifyContent: "center",
            alignItems: "center",
            gap: "8px",
          }}>
          {indexedData.map((token) => {
            const priceUsd = NATIVE_CURRENCY_CHECK_LIST.includes(token?.symbol)
              ? token?.priceUsd - (minimumGasBufferUSD[token.chain as Chain] ?? 0)
              : token?.priceUsd;

            if (priceUsd < 0) {
              return null;
            }

            return (
              <Box
                key={token.index}
                sx={{
                  display: "flex",
                  justifyContent: "space-between",
                  alignItems: "center",
                  width: "100%",
                  borderRadius: "16px",
                  border: `1px solid ${theme.palette.border.dark}`,
                  background: theme.palette.surface.silver,
                  padding: "12px",
                }}
                onClick={() => {
                  setSelectedTokenIndex(token.index);
                  handleCloseSearchDrawer();
                }}>
                <Box
                  sx={{
                    display: "flex",
                    alignItems: "center",
                    justifyContent: "space-between",
                    gap: "16px",
                  }}>
                  <CoinAvatar
                    style={{
                      width: "28px",
                      height: "28px",
                    }}
                    badgeStyle={{
                      width: "14px",
                      height: "14px",
                    }}
                    iconUri={token.imageUrl ? token.imageUrl : NATIVE_TOKEN_MAP[token.symbol]}
                    chain={token.chain}
                    size="sm"
                  />
                  <Typography variant="subtitle">{token.symbol}</Typography>
                </Box>
                <Value variant="body-default" value={priceUsd} isPrice />
              </Box>
            );
          })}
        </Box>
      </DarkDrawer>
      <DarkDrawer
        open={isOpenConfirmDrawer}
        anchor="bottom"
        onClose={() => {}}
        sx={{ zIndex: 200 }}>
        <Box>
          <Typography variant="title-h2">CONFIRM SENDING?</Typography>
        </Box>
        <Box
          sx={{
            display: "flex",
            justifyContent: "start",
            alignItems: "center",
            marginTop: "16px",
            marginBottom: "32px",
            gap: "8px",
          }}>
          <Typography variant="body-md" color={theme.palette.text.secondary}>
            Sending
          </Typography>
          <Value variant="body-md" value={amount} isPrice />
          <Typography variant="body-md" color={theme.palette.text.secondary}>
            {indexedData[selectedTokenIndex] ? indexedData[selectedTokenIndex].symbol : "N/A"} To{" "}
            {address}
          </Typography>
        </Box>
        <Box
          sx={{
            display: "flex",
            justifyContent: "space-between",
            alignItems: "center",
            gap: "10px",
          }}>
          <TextButton
            variant="white"
            typo="title-h3"
            typoColor={isSending ? "text.secondary" : "text.btn-dark"}
            disabled={isSending}
            onClick={handleCloseConfirmDrawer}
            fullWidth
            innerSx={{
              paddingY: theme.spacing(1),
              width: "100%",
            }}>
            NO
          </TextButton>
          <TextButton
            typo="title-h3"
            variant={isSending ? "disabled" : "primary"}
            typoColor={isSending ? "text.secondary" : "text.brand"}
            onClick={handleConfirm}
            disabled={isSending}
            fullWidth
            innerSx={{
              paddingY: theme.spacing(1),
              width: "100%",
            }}>
            {isSending ? "SENDING..." : "YES"}
          </TextButton>
        </Box>
      </DarkDrawer>
    </>
  );
};

// export const SendTokenDrawer = <></>;
